import React from 'react'
import { Child } from '../../../providers/context'
import Delta from 'quill-delta'
import { UploadedFile } from '../../../components/media/fileUpload'
import { Recording } from '../../../components/media/audioRecorder'
import { LanguageGlobe } from '../messageTags'
import { BasicUserGroup, BasicUser } from '../../../components/userSelector/users'
import { singleSelectedRecipient } from './composeHelpers'
import { Language } from '../../../providers/languages'

export type SendOption = {
  name: string
  type:
    | 'send'
    | 'confirm'
    | 'later'
    | 'draft'
    | 'test'
    | 'call'
    | 'schedule-video'
    | 'call-interpreted'
    | 'schedule-interpreted'
  forUser?: 'teacher' | 'guardian'
  tooltip?: React.ReactNode
  forPhone?: boolean
  sendTypesDoesntHave?: SendType
  allowExtraInvites?: boolean
  forLexikeet?: boolean
  shouldAutoSwitchTo?: (cs: ComposeState) => boolean
  requiresIndividualRecipient?: boolean
}

export const SendOptions: SendOption[] = [
  { name: 'Send', type: 'send' },
  {
    name: 'Send Confirm Receipt',
    type: 'confirm',
    forUser: 'teacher',
    tooltip: (
      <div>
        Get confirmation that each recipient opens your message. <br />
        <b>Note:</b> This requires users to click a link to view this message.
      </div>
    ),
    sendTypesDoesntHave: 'mass',
  },
  { name: 'Send Later', type: 'later' },
  { name: 'Save Draft', type: 'draft' },
  { name: 'Send Test', type: 'test', forUser: 'teacher' },
  { name: 'Call', type: 'call', forPhone: true, forUser: 'teacher' },
  {
    name: 'Schedule Video Call',
    type: 'schedule-video',
    forPhone: true,
    forUser: 'teacher',
    allowExtraInvites: true,
    shouldAutoSwitchTo: (cs: ComposeState) => {
      const someoneSelected = singleSelectedRecipient(cs)
      if (someoneSelected || cs.selectedRecipients.length !== 1) return false
      const selected = cs.selectedRecipients[0] as StudentRecipient // only for teacher, so this is fine
      if (selected.group) return false
      return !selected.contactMethods.phone && !selected.guardians.some((g) => g.contactMethods.phone)
    },
    requiresIndividualRecipient: true, // cant send with just staff selected
  },
  {
    name: 'Video Call With Interpreter',
    type: 'call-interpreted',
    forPhone: true,
    forUser: 'teacher',
    allowExtraInvites: true,
    forLexikeet: true,
    shouldAutoSwitchTo: (cs: ComposeState) => singleSelectedRecipient(cs)?.lk === 'all',
  },
  {
    name: 'Schedule Video Call With Interpreter',
    type: 'schedule-interpreted',
    forPhone: true,
    forUser: 'teacher',
    allowExtraInvites: true,
    forLexikeet: true,
    shouldAutoSwitchTo: (cs: ComposeState) => singleSelectedRecipient(cs)?.lk === 'scheduled',
  },
]

export type SendType = 'email' | 'text' | 'phone' | 'mass'

type GroupContactableData = {
  studentsWithoutReachableParents: string[]
  unreachableStudents: string[]
  studentsWithoutReachableParentsMass?: string[]
  unreachableStudentsMass?: string[]
}

type StudentContactableData = {
  selfHasNoReachableParents: boolean
  unreachableSelf: boolean
  selfHasNoReachableParentsMass?: boolean
  unreachableSelfMass?: boolean
}

export type ContactMethods = {
  email: boolean
  phone: boolean
  massPhone: boolean
  elementary: boolean
  text: boolean
  whatsapp: boolean
  language: string
}

type RecipientFilterOnTab = {
  filterOnTab?: 'mass'
}

export type StudentGroupRecipient = BasicUserGroup & {
  guardianIDs?: number[]
  contactable: GroupContactableData
  schoolID?: number
} & RecipientFilterOnTab

type LKModes = 'selectLanguage' | 'all' | 'scheduled'

export type SingleStudentRecipient = BasicUser &
  RecipientFilterOnTab & {
    contactable: StudentContactableData
    contactMethods: ContactMethods
    lk?: LKModes
    globe?: LanguageGlobe
    guardians: {
      id: number
      name: string
      contactMethods: ContactMethods
      relationship: string
      globe?: LanguageGlobe
      lk?: LKModes
    }[]
  }

export type StudentRecipient = SingleStudentRecipient | StudentGroupRecipient

export type SingleTeacherRecipient = BasicUser &
  RecipientFilterOnTab & {
    schoolIDs: number[]
    foundByStudents?: number[]
  }

export type TeacherGroupRecipient = BasicUserGroup & RecipientFilterOnTab & {}

export type TeacherRecipient = SingleTeacherRecipient | TeacherGroupRecipient

export type SharedSingleTeacherRecipient = BasicUser &
  RecipientFilterOnTab & {
    info?: string // raw html??
  }

export type SharedTeacherGroupRecipient = BasicUserGroup &
  RecipientFilterOnTab & {
    teacherCount?: number
  }

export type Recipient = StudentRecipient | TeacherRecipient
export type SharedRecipient = SharedSingleTeacherRecipient | SharedTeacherGroupRecipient

export interface RecipientsInfo {
  toStudents: boolean
  toGuardians: boolean
  toSharedTeachers: boolean
  sharedTeacherRecipients: SharedRecipient[]
  individuals: StudentGuardianRecipient[]
  extraInvites: StudentGuardianRecipient[]
}

export const DEFAULT_RECIPIENTS_INFO = {
  toStudents: false,
  toGuardians: false,
  toSharedTeachers: false,
  sharedTeacherRecipients: [],
  individuals: [],
  extraInvites: [],
}

export type ComposeState = {
  sendTypes: Set<SendType>
  massAllowReplying: boolean
  sharedTeachersSeeReplies: boolean
  selectedRecipients: Recipient[]
  recipientsInfo: RecipientsInfo
  subject: string
  sendOption: SendOption
  bodyHTML?: string
  bodyDelta?: Delta
  previewingTemplate: boolean
  aiSuggestion?: string
  attachments: UploadedFile[]
  regardingStudent?: Child
  scheduledTimes?: Date[]
  scheduledTimeNote?: string
  sendLaterDate?: Date
  testUser?: {
    email: string
    phoneNumber?: string
    language: string
  }
  lexikeetCallLanguage?: Language
  callSuggestingTimes?: boolean
  recording?: Recording
  draftID?: number
  initiallySelectedRecipients?: {
    users?: number[]
    groups?: (number | string)[]
    individuals?: number[]
    shared?: (number | string)[]
  }
  emergency?: boolean
  lexikeetInPerson?: boolean
}

export const DEFAULT_COMPOSE_STATE: ComposeState = {
  sendTypes: new Set(['email']),
  massAllowReplying: false,
  sharedTeachersSeeReplies: false,
  selectedRecipients: [],
  recipientsInfo: DEFAULT_RECIPIENTS_INFO,
  subject: '',
  sendOption: { name: 'Send', type: 'send' },
  bodyHTML: '<p><br></p>',
  previewingTemplate: false,
  attachments: [],
}
export interface StudentGuardianRecipient {
  studentID: number
  guardianID?: number
}
